/* eslint-disable no-mixed-operators */
import numeral from 'numeral';
import {
    parseISO, format, isValid, startOfMonth, formatISO, parse,
} from 'date-fns';
import { isNaN } from 'lodash';
import { Nullable } from '../types/shared';
// ------- Currency Formatter ---------

export const formatUS = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

export const formatUSTwoDigits = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formCanada = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

export const formatDateToYearMonth = (dateString: string | null): string | null => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
};

export const parseYearMonthToDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const parsed = parse(dateStr, 'yyyy-MM', new Date());
    return isValid(parsed) ? parsed : null;
};

export const formatUSDCurrency = (value: Nullable<number>, showDashWhenEmpty: boolean = false) => {
    if (value) {
        let formatted = formatUS.format(value);
        if (formatted === '-$0') {
            formatted = '$0';
        }
        return formatted;
    }
    return showDashWhenEmpty ? '-' : '$0';
};

export const formatUSDCurrencyTwoDigits = (value: number | string | undefined, decimal: boolean = false) => {
    let returnValue = '$0';
    if (decimal) {
        returnValue = '$0.00';
    }
    if (value) {
        let formatted = returnValue;
        if (typeof value !== 'string') {
            formatted = formatUSTwoDigits.format(value);
        } else if (!Number.isNaN(parseFloat(value))) {
            formatted = formatUSTwoDigits.format(parseFloat(value));
        }

        if (formatted === '-$0.00' || formatted === '$0.00') {
            return returnValue;
        }
        return formatted;
    }
    return returnValue;
};

export const formatCanadaCurrency = (value: number) => {
    let formatted = formCanada.format(value);
    if (formatted === '-$0') {
        formatted = '$0';
    }
    return formatted;
};

/*
 * This function implements workarounds for bugs in the numeral.js library.
 * 1) Negative values that round to zero have incorrect formatting
 *    (https://github.com/adamwdraper/Numeral-js/issues/777)
 * 2) Extremely small results return NaN
 *    (https://github.com/adamwdraper/Numeral-js/issues/512)
 */
export const numeralFixed = (value: number | string | undefined, format: string) => {
    // Numeral.format() accepts a custom rounding function, which is where both of these
    // issues occur, so we can use this to detect or intercept values that will fail,
    // and handle these cases accordingly

    let roundsToZero; // will be true if the rounding function returns 0

    const customRounder = (num: string): number => {
        // The odd typing here is to most accurately represent what numeral.js is doing
        // under the hood, which is passing a numeric value as a string to the rounding
        // function, and expecting the rounding function to coerce the value to a number.

        // The library uses Math.round as the default rounding function, so try that first
        const defaultResult = Math.round(num as any);
        if (defaultResult === 0) {
            roundsToZero = true;
        }
        if (!Number.isNaN(defaultResult)) {
            return defaultResult;
        }

        // Numeral appends '+e' to the arg passed into this function, so if the original
        // value already has '+e' (as JS does automatically for very small numbers),
        // the argument will be something like "-1.1641532182693481e-10e+2" which cannot
        // be coerced to a number unless the exponents are split up and calculated manually.
        const parts = num.split('e');
        if (parts.length === 3) {
            const [coef, exponent1, exponent2] = parts.map(e => Number(e));
            const e = 10;
            const fixedResult = Math.round((coef * e) ** (exponent1 + exponent2));
            if (fixedResult === 0) {
                roundsToZero = true;
            }
            return fixedResult;
        }
        return NaN;
    };
    const defaultResult = numeral(value).format(format, customRounder);
    // if the result would have rounded to zero, just pass in 0 and return that instead
    return roundsToZero ? numeral(0).format(format) : defaultResult;
};

export const numeralFormatterCurrency = (value: number | string | undefined, format: string = '$0,0.00') => numeralFixed(value, format);
export const numeralFormatter = (value: number | string | undefined, format: string = '0,0') => numeralFixed(value, format);

export const isFormattedCurrency = (value: any): boolean => {
    const stringValue = value != null ? value.toString() : '';
    const currencyRegex = /^-?\$?(?:\d+|\d{1,3}(,\d{3})+)(\.\d+)?$/; // Examples: $1,234.56, -$1,234, $-1,234.56, or -1,234
    return currencyRegex.test(stringValue);
};

export const parseCurrencyToNumber = (formattedValue: string | number): number => {
    const stringValue = formattedValue != null ? formattedValue.toString() : '';
    const cleanedValue = stringValue.replace(/[^0-9.-]+/g, '');
    return parseFloat(cleanedValue);
};

// ------- Percentage ---------

export const formatPercentageNumber = (numerator: number, denominator: number, decimals: number = 0) => {
    if (denominator === 0) {
        return 0;
    }
    return Number(((numerator / denominator) * 100).toFixed(decimals));
};

export const formatDecimalAsPercentage = (value: string | number, decimals: number = 0) => {
    const numericValue = typeof value === 'string' ? Number(value) : value;
    return `${(numericValue * 100).toFixed(decimals)}%`;
};

export const formatPercentage = (
    numerator: number, denominator: number, decimals: number,
) => `${formatPercentageNumber(numerator, denominator, decimals).toFixed(decimals)}%`;

export const percentFormatter = (value: number | string | undefined): string | undefined => {
    if (value === undefined || value === '' || value === null) {
        return undefined;
    }

    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    return `${numeralFixed(numericValue, '0,0.00')}%`;
};

// ------- Date ---------

export const DATE_FORMAT_OPTIONS_WITH_TIME: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
};

export const formatDateShort = (date: string | null | undefined): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' });
};

export const formatDateLong = (
    isoStringOrDate: string | Date,
    options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string => {
    const date = typeof isoStringOrDate === 'string' ? new Date(isoStringOrDate) : isoStringOrDate;
    return new Intl.DateTimeFormat('en-US', options).format(date);
};

// Expects an ISO 8601 datetime string with a timezone indicator (e.g., 'Z' or '+00:00'),
// or a Date whose value already represents the correct instant in time; output is shown in local time.
export const formatUTCtoLocal = (date: string | Date = new Date(), formatStr: string = 'MMMM d yyyy h:mma') => {
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    // date-fns format() automatically displays in the browser's local timezone
    return format(parsedDate, formatStr);
};

export const formatLocalToUTC = (date: string | Date = new Date(), formatStr: string = "yyyy-MM-dd'T'HH:mm:ss'Z'") => {
    let parsedDate: Date;

    if (typeof date === 'string') {
        parsedDate = parseISO(date);
        if (!isValid(parsedDate)) {
            throw new RangeError('Invalid date string');
        }
    } else {
        parsedDate = date;
    }

    const utcDate = new Date(parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000);

    return format(utcDate, formatStr);
};

export const isValidDate = (date: any): boolean => date instanceof Date && !isNaN(date.getTime());

const parseDateString = (date: string): Date | null => {
    const parsedDate = new Date(date);
    return isValidDate(parsedDate) ? parsedDate : null;
};

export const isValidDateString = (date: any): boolean => {
    if (typeof date !== 'string') return false;

    // ISO 8601 format check with optional time and timezone
    const iso8601Regex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;
    if (iso8601Regex.test(date)) {
        return !!parseDateString(date); // Parse and ensure validity
    }

    return false;
};

export const formatISOStartOfMonth = (isoString: string) => {
    const parsedDate = parseISO(isoString);
    return formatISO(startOfMonth(parsedDate), { representation: 'date' });
};

// ------- Size Formatting ---------
export const formatFileSize = (sizeInBytes: number): string => {
    let sizeInKiloBytes = sizeInBytes / 1000;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let unitIndex = 0;

    while (sizeInKiloBytes >= 1000 && unitIndex < units.length - 1) {
        sizeInKiloBytes /= 1000;
        unitIndex++;
    }

    return `${sizeInKiloBytes.toFixed(1)} ${units[unitIndex]}`;
};

// ------- Word Formatting ---------
export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const camelToSnakeCase = (str: string): string => str.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();

export const sentenceToSnakeCase = (str: string): string => str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

export const snakeToSentenceCase = (str: string): string => str
    .split('_')
    .map((word, index) => (index === 0 ? capitalizeFirstLetter(word) : word))
    .join(' ');

export const camelToSentenceCase = (str: string): string => str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, match => match.toUpperCase());

// ------- Miscellaneous ---------
export const bpsFormatter = (value: number | string | undefined): string => {
    if (value === undefined || value === '' || value === null) {
        return '-';
    }

    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numericValue)) {
        return '-';
    }

    return `${numeralFixed(numericValue, '0,0')} bps`;
};
